import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import DashboardLayout from '../components/layout/DashboardLayout';
import { useAuth, API } from '../App';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  Share2, Download, Image, Trophy, Flame, Zap, 
  Target, Dumbbell, Apple, Moon, CheckCircle,
  Copy, Twitter, Instagram, Facebook, Loader2,
  Sparkles, TrendingUp
} from 'lucide-react';
import { toPng } from 'html-to-image';
import download from 'downloadjs';
import axios from 'axios';
import { toast } from 'sonner';

// Card Templates
const TEMPLATES = [
  { 
    id: 'titan', 
    name: 'The Titan', 
    description: 'Bold focus on one big stat',
    icon: Flame,
    color: 'from-amber-500 to-yellow-600'
  },
  { 
    id: 'grid', 
    name: 'The Grid', 
    description: 'Bento-style multi-stat layout',
    icon: Target,
    color: 'from-blue-500 to-cyan-500'
  },
  { 
    id: 'badge', 
    name: 'Badge Unlock', 
    description: 'Celebrate a recent achievement',
    icon: Trophy,
    color: 'from-yellow-500 to-amber-600'
  }
];

const SIZES = [
  { id: 'post', label: 'Post (1:1)', width: 1080, height: 1080 },
  { id: 'story', label: 'Story (9:16)', width: 1080, height: 1920 }
];

// The Titan Template - Single Big Stat
const TitanCard = ({ stats, user, size }) => {
  const isStory = size.id === 'story';
  
  return (
    <div 
      className="relative overflow-hidden"
      style={{
        width: isStory ? '270px' : '300px',
        height: isStory ? '480px' : '300px',
        background: 'linear-gradient(135deg, #18181B 0%, #09090B 100%)',
        fontFamily: 'Inter, sans-serif'
      }}
    >
      {/* Glow Effect */}
      <div 
        className="absolute inset-0 opacity-30"
        style={{
          background: 'radial-gradient(circle at 50% 30%, rgba(212, 175, 55, 0.3) 0%, transparent 60%)'
        }}
      />
      
      {/* Content */}
      <div className={`relative h-full flex flex-col ${isStory ? 'justify-center' : 'justify-center'} items-center p-6 text-center`}>
        <p className="text-xs text-zinc-400 uppercase tracking-[0.3em] mb-2">
          {user?.name || 'ATHLETE'}
        </p>
        
        <div className="my-4">
          <p className={`font-black text-white ${isStory ? 'text-8xl' : 'text-7xl'}`} style={{ fontFamily: 'Inter, sans-serif', letterSpacing: '-0.02em' }}>
            {stats?.streak || 0}
          </p>
          <p className="text-lg text-amber-500 uppercase tracking-widest font-bold mt-2">
            Day Streak
          </p>
        </div>
        
        <div className="flex items-center gap-2 mt-4">
          <Flame className="w-5 h-5 text-amber-500" />
          <span className="text-zinc-400 text-sm">On Fire</span>
        </div>
        
        {/* Watermark */}
        <p className="absolute bottom-4 text-[10px] text-zinc-600 tracking-wider">
          auraapp.fit
        </p>
      </div>
    </div>
  );
};

// The Grid Template - Multiple Stats
const GridCard = ({ stats, user, size }) => {
  const isStory = size.id === 'story';
  
  const gridStats = [
    { label: 'Workouts', value: stats?.workouts_this_week || 0, icon: Dumbbell, color: 'text-amber-500' },
    { label: 'Calories', value: stats?.today_calories || 0, icon: Apple, color: 'text-green-500' },
    { label: 'Recovery', value: stats?.recovery_score || 0, icon: Moon, color: 'text-cyan-500' },
    { label: 'Aura', value: stats?.aura_score || 0, icon: Trophy, color: 'text-yellow-500' }
  ];
  
  return (
    <div 
      className="relative overflow-hidden"
      style={{
        width: isStory ? '270px' : '300px',
        height: isStory ? '480px' : '300px',
        background: '#09090B',
        fontFamily: 'Inter, sans-serif'
      }}
    >
      {/* Subtle Grid Pattern */}
      <div 
        className="absolute inset-0 opacity-5"
        style={{
          backgroundImage: 'linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)',
          backgroundSize: '40px 40px'
        }}
      />
      
      {/* Header */}
      <div className={`relative p-4 ${isStory ? 'pt-8' : ''}`}>
        <p className="text-xs text-zinc-500 uppercase tracking-[0.2em]">Weekly Stats</p>
        <p className="text-lg font-bold text-white uppercase tracking-tight">{user?.name || 'ATHLETE'}</p>
      </div>
      
      {/* Stats Grid */}
      <div className={`relative px-4 ${isStory ? 'py-8' : 'pb-4'}`}>
        <div className={`grid grid-cols-2 gap-3`}>
          {gridStats.map((stat, idx) => (
            <div 
              key={idx}
              className="p-3 rounded-lg"
              style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}
            >
              <stat.icon className={`w-4 h-4 ${stat.color} mb-2`} />
              <p className="text-2xl font-bold text-white" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                {stat.value}
              </p>
              <p className="text-[10px] text-zinc-500 uppercase tracking-wider">{stat.label}</p>
            </div>
          ))}
        </div>
      </div>
      
      {/* Watermark */}
      <p className="absolute bottom-3 left-0 right-0 text-center text-[10px] text-zinc-700 tracking-wider">
        auraapp.fit
      </p>
    </div>
  );
};

// Badge Unlock Template
const BadgeCard = ({ stats, user, size, recentBadge }) => {
  const isStory = size.id === 'story';
  
  const badge = recentBadge || { name: 'First Workout', tier: 'bronze', points: 10 };
  const tierColors = {
    bronze: { bg: 'rgba(180, 83, 9, 0.2)', border: 'rgba(217, 119, 6, 0.5)', text: '#D97706' },
    silver: { bg: 'rgba(148, 163, 184, 0.2)', border: 'rgba(148, 163, 184, 0.5)', text: '#94A3B8' },
    gold: { bg: 'rgba(234, 179, 8, 0.2)', border: 'rgba(234, 179, 8, 0.5)', text: '#EAB308' }
  };
  const colors = tierColors[badge.tier] || tierColors.bronze;
  
  return (
    <div 
      className="relative overflow-hidden"
      style={{
        width: isStory ? '270px' : '300px',
        height: isStory ? '480px' : '300px',
        background: '#09090B',
        fontFamily: 'Inter, sans-serif'
      }}
    >
      {/* Radial Glow */}
      <div 
        className="absolute inset-0"
        style={{
          background: `radial-gradient(circle at 50% ${isStory ? '40%' : '50%'}, ${colors.bg} 0%, transparent 60%)`
        }}
      />
      
      {/* Content */}
      <div className={`relative h-full flex flex-col items-center ${isStory ? 'justify-center' : 'justify-center'} p-6`}>
        <p className="text-xs text-zinc-500 uppercase tracking-[0.3em] mb-4">
          Badge Unlocked
        </p>
        
        {/* Badge Icon */}
        <div 
          className="w-24 h-24 rounded-full flex items-center justify-center mb-4"
          style={{ 
            background: colors.bg, 
            border: `2px solid ${colors.border}`,
            boxShadow: `0 0 40px ${colors.bg}`
          }}
        >
          <Trophy className="w-12 h-12" style={{ color: colors.text }} />
        </div>
        
        <p className="text-xl font-bold text-white uppercase tracking-tight text-center">
          {badge.name}
        </p>
        
        <div className="flex items-center gap-2 mt-2">
          <span 
            className="text-xs uppercase tracking-wider px-2 py-1 rounded-full"
            style={{ background: colors.bg, color: colors.text, border: `1px solid ${colors.border}` }}
          >
            {badge.tier}
          </span>
          <span className="text-zinc-500 text-sm">+{badge.points} pts</span>
        </div>
        
        <p className="text-xs text-zinc-500 mt-4">{user?.name || 'ATHLETE'}</p>
        
        {/* Watermark */}
        <p className="absolute bottom-4 text-[10px] text-zinc-700 tracking-wider">
          auraapp.fit
        </p>
      </div>
    </div>
  );
};

const ShareAuraPage = () => {
  const { user } = useAuth();
  const cardRef = useRef(null);
  const [selectedTemplate, setSelectedTemplate] = useState('titan');
  const [selectedSize, setSelectedSize] = useState(SIZES[0]);
  const [stats, setStats] = useState(null);
  const [badges, setBadges] = useState([]);
  const [recentBadge, setRecentBadge] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('aura_token');
      const headers = { Authorization: `Bearer ${token}` };
      
      // Fetch dashboard stats
      const statsRes = await axios.get(`${API}/dashboard/stats`, { headers });
      
      // Fetch badges
      const badgesRes = await axios.get(`${API}/badges`, { headers });
      
      const earnedBadges = badgesRes.data.badges?.filter(b => b.earned) || [];
      
      setStats({
        ...statsRes.data,
        aura_score: badgesRes.data.total_points || 0,
        streak: statsRes.data.streak || 7 // Fallback for demo
      });
      setBadges(earnedBadges);
      setRecentBadge(earnedBadges[earnedBadges.length - 1] || null);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load stats');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async () => {
    if (!cardRef.current) return;
    
    setGenerating(true);
    try {
      // Scale up for quality
      const scale = 3;
      const dataUrl = await toPng(cardRef.current, {
        quality: 0.95,
        pixelRatio: scale,
        backgroundColor: '#09090B'
      });
      
      download(dataUrl, `aura-${selectedTemplate}-${Date.now()}.png`);
      toast.success('Image downloaded!');
    } catch (error) {
      console.error('Failed to generate image:', error);
      toast.error('Failed to generate image');
    } finally {
      setGenerating(false);
    }
  };

  const handleShare = async () => {
    if (!cardRef.current) return;
    
    setGenerating(true);
    try {
      const dataUrl = await toPng(cardRef.current, {
        quality: 0.95,
        pixelRatio: 3,
        backgroundColor: '#09090B'
      });
      
      // Convert to blob
      const res = await fetch(dataUrl);
      const blob = await res.blob();
      const file = new File([blob], 'aura-stats.png', { type: 'image/png' });
      
      if (navigator.share && navigator.canShare?.({ files: [file] })) {
        await navigator.share({
          title: 'My Aura Stats',
          text: 'Check out my athletic development on Aura! auraapp.fit #AuraIsEarned #AuraApp',
          files: [file]
        });
        toast.success('Shared successfully!');
      } else {
        // Fallback: copy to clipboard or download
        await navigator.clipboard.write([
          new ClipboardItem({ 'image/png': blob })
        ]);
        toast.success('Image copied to clipboard!');
      }
    } catch (error) {
      console.error('Share failed:', error);
      // If clipboard fails, just download
      handleDownload();
    } finally {
      setGenerating(false);
    }
  };

  const renderCard = () => {
    const props = { stats, user, size: selectedSize, recentBadge };
    
    switch (selectedTemplate) {
      case 'grid':
        return <GridCard {...props} />;
      case 'badge':
        return <BadgeCard {...props} />;
      default:
        return <TitanCard {...props} />;
    }
  };

  if (loading) {
    return (
      <DashboardLayout title="Share Your Aura">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-aura-primary" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Share Your Aura">
      <div className="px-4 py-6 max-w-5xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="font-heading font-bold text-2xl sm:text-3xl text-white uppercase tracking-tight">
            Share Your Aura
          </h1>
          <p className="text-aura-text-muted mt-2">
            Aura is earned, not given. Show the world your progress.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Controls Panel */}
          <div className="space-y-6">
            {/* Template Selection */}
            <Card className="bg-aura-surface border-white/10">
              <CardContent className="p-4">
                <p className="text-xs text-aura-text-muted uppercase tracking-wider mb-4">Choose Template</p>
                <div className="grid grid-cols-3 gap-3">
                  {TEMPLATES.map((template) => (
                    <button
                      key={template.id}
                      onClick={() => setSelectedTemplate(template.id)}
                      className={`p-3 rounded-xl text-center transition-all ${
                        selectedTemplate === template.id
                          ? `bg-gradient-to-br ${template.color} border-2 border-white/20`
                          : 'bg-white/5 border border-white/10 hover:border-white/20'
                      }`}
                      data-testid={`template-${template.id}`}
                    >
                      <template.icon className={`w-6 h-6 mx-auto mb-2 ${selectedTemplate === template.id ? 'text-white' : 'text-aura-text-muted'}`} />
                      <p className={`text-xs font-medium ${selectedTemplate === template.id ? 'text-white' : 'text-aura-text-muted'}`}>
                        {template.name}
                      </p>
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Size Selection */}
            <Card className="bg-aura-surface border-white/10">
              <CardContent className="p-4">
                <p className="text-xs text-aura-text-muted uppercase tracking-wider mb-4">Card Size</p>
                <div className="flex gap-3">
                  {SIZES.map((size) => (
                    <button
                      key={size.id}
                      onClick={() => setSelectedSize(size)}
                      className={`flex-1 p-3 rounded-lg text-center transition-all ${
                        selectedSize.id === size.id
                          ? 'bg-aura-primary text-white'
                          : 'bg-white/5 text-aura-text-muted hover:bg-white/10'
                      }`}
                      data-testid={`size-${size.id}`}
                    >
                      <p className="text-sm font-medium">{size.label}</p>
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Stats Preview */}
            <Card className="bg-aura-surface border-white/10">
              <CardContent className="p-4">
                <p className="text-xs text-aura-text-muted uppercase tracking-wider mb-4">Your Stats</p>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div className="flex items-center gap-2">
                    <Dumbbell className="w-4 h-4 text-amber-500" />
                    <span className="text-white">{stats?.workouts_this_week || 0} Workouts</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Trophy className="w-4 h-4 text-yellow-500" />
                    <span className="text-white">{stats?.aura_score || 0} Aura</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Flame className="w-4 h-4 text-amber-500" />
                    <span className="text-white">{stats?.streak || 0} Day Streak</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Moon className="w-4 h-4 text-cyan-500" />
                    <span className="text-white">{stats?.recovery_score || 0} Recovery</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Action Buttons */}
            <div className="flex gap-3">
              <Button
                onClick={handleDownload}
                disabled={generating}
                className="flex-1 bg-aura-surface border border-white/10 text-white hover:bg-white/10"
                data-testid="download-btn"
              >
                {generating ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Download className="w-4 h-4 mr-2" />
                )}
                Download
              </Button>
              <Button
                onClick={handleShare}
                disabled={generating}
                className="flex-1 bg-aura-primary hover:bg-aura-primary-hover text-white"
                data-testid="share-btn"
              >
                {generating ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Share2 className="w-4 h-4 mr-2" />
                )}
                Share
              </Button>
            </div>

            {/* Social Links */}
            <div className="flex justify-center gap-4">
              <a 
                href={`https://twitter.com/intent/tweet?text=${encodeURIComponent('Check out my athletic progress on Aura! auraapp.fit #AuraApp #EliteAthlete')}`}
                target="_blank"
                rel="noopener noreferrer"
                className="p-3 rounded-full bg-white/5 hover:bg-white/10 transition-colors"
                data-testid="twitter-share"
              >
                <Twitter className="w-5 h-5 text-aura-text-muted hover:text-white" />
              </a>
              <button 
                onClick={() => toast.info('Save the image and share to Instagram!')}
                className="p-3 rounded-full bg-white/5 hover:bg-white/10 transition-colors"
                data-testid="instagram-share"
              >
                <Instagram className="w-5 h-5 text-aura-text-muted hover:text-white" />
              </button>
              <a 
                href={`https://www.facebook.com/sharer/sharer.php?quote=${encodeURIComponent('Check out my athletic progress on Aura! auraapp.fit')}`}
                target="_blank"
                rel="noopener noreferrer"
                className="p-3 rounded-full bg-white/5 hover:bg-white/10 transition-colors"
                data-testid="facebook-share"
              >
                <Facebook className="w-5 h-5 text-aura-text-muted hover:text-white" />
              </a>
            </div>
          </div>

          {/* Preview Panel */}
          <div className="flex flex-col items-center">
            <p className="text-xs text-aura-text-muted uppercase tracking-wider mb-4">Preview</p>
            
            {/* Card Container */}
            <div 
              className="relative bg-zinc-900 rounded-xl p-4 flex items-center justify-center"
              style={{ 
                minHeight: selectedSize.id === 'story' ? '520px' : '340px',
                minWidth: '340px'
              }}
            >
              {/* The actual card to capture */}
              <div ref={cardRef}>
                {renderCard()}
              </div>
            </div>
            
            <p className="text-xs text-aura-text-muted mt-4">
              Actual size: {selectedSize.width} x {selectedSize.height}px
            </p>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default ShareAuraPage;
